<script setup>
import { reactive, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useVuelidate } from '@vuelidate/core';
import { required, url } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';
import { useStore, useMapGetter } from 'dashboard/composables/store';

import PageHeader from '../../SettingsSubPageHeader.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

const { t } = useI18n();
const store = useStore();
const router = useRouter();

const state = reactive({
  channelName: '',
  redlabCallsUrl: '',
  redlabCallsApiKey: '',
  extension: '',
  phoneNumber: '',
});

const uiFlags = useMapGetter('inboxes/getUIFlags');

const validationRules = {
  channelName: { required },
  redlabCallsUrl: { required, url },
  redlabCallsApiKey: { required },
  extension: { required },
};

const v$ = useVuelidate(validationRules, state);
const isSubmitDisabled = computed(() => v$.value.$invalid);

const formErrors = computed(() => ({
  channelName: v$.value.channelName?.$error
    ? t('INBOX_MGMT.ADD.REDLAB_CALLS.CHANNEL_NAME.ERROR')
    : '',
  redlabCallsUrl: v$.value.redlabCallsUrl?.$error
    ? t('INBOX_MGMT.ADD.REDLAB_CALLS.URL.ERROR')
    : '',
  redlabCallsApiKey: v$.value.redlabCallsApiKey?.$error
    ? t('INBOX_MGMT.ADD.REDLAB_CALLS.API_KEY.ERROR')
    : '',
  extension: v$.value.extension?.$error
    ? t('INBOX_MGMT.ADD.REDLAB_CALLS.EXTENSION.ERROR')
    : '',
}));

async function createChannel() {
  const isFormValid = await v$.value.$validate();
  if (!isFormValid) return;

  try {
    const inbox = await store.dispatch('inboxes/createRedlabCallsChannel', {
      redlab_calls_channel: {
        name: state.channelName.trim(),
        redlab_calls_url: state.redlabCallsUrl.trim(),
        redlab_calls_api_key: state.redlabCallsApiKey.trim(),
        extension: state.extension.trim(),
        phone_number: state.phoneNumber.trim() || null,
      },
    });

    router.replace({
      name: 'settings_inboxes_add_agents',
      params: { page: 'new', inbox_id: inbox.id },
    });
  } catch (error) {
    useAlert(
      error.response?.data?.message ||
        error.message ||
        t('INBOX_MGMT.ADD.REDLAB_CALLS.API.ERROR_MESSAGE')
    );
  }
}
</script>

<template>
  <div class="overflow-auto col-span-6 p-6 w-full h-full">
    <PageHeader
      :header-title="t('INBOX_MGMT.ADD.REDLAB_CALLS.TITLE')"
      :header-content="t('INBOX_MGMT.ADD.REDLAB_CALLS.DESC')"
    />

    <form
      class="flex flex-col gap-4 flex-wrap mx-0"
      @submit.prevent="createChannel"
    >
      <Input
        v-model="state.channelName"
        :label="t('INBOX_MGMT.ADD.REDLAB_CALLS.CHANNEL_NAME.LABEL')"
        :placeholder="t('INBOX_MGMT.ADD.REDLAB_CALLS.CHANNEL_NAME.PLACEHOLDER')"
        :message="formErrors.channelName"
        :message-type="formErrors.channelName ? 'error' : 'info'"
        @blur="v$.channelName?.$touch"
      />

      <Input
        v-model="state.redlabCallsUrl"
        :label="t('INBOX_MGMT.ADD.REDLAB_CALLS.URL.LABEL')"
        :placeholder="t('INBOX_MGMT.ADD.REDLAB_CALLS.URL.PLACEHOLDER')"
        :message="
          formErrors.redlabCallsUrl ||
          t('INBOX_MGMT.ADD.REDLAB_CALLS.URL.SUBTITLE')
        "
        :message-type="formErrors.redlabCallsUrl ? 'error' : 'info'"
        @blur="v$.redlabCallsUrl?.$touch"
      />

      <Input
        v-model="state.redlabCallsApiKey"
        type="password"
        :label="t('INBOX_MGMT.ADD.REDLAB_CALLS.API_KEY.LABEL')"
        :placeholder="t('INBOX_MGMT.ADD.REDLAB_CALLS.API_KEY.PLACEHOLDER')"
        :message="formErrors.redlabCallsApiKey"
        :message-type="formErrors.redlabCallsApiKey ? 'error' : 'info'"
        @blur="v$.redlabCallsApiKey?.$touch"
      />

      <Input
        v-model="state.extension"
        :label="t('INBOX_MGMT.ADD.REDLAB_CALLS.EXTENSION.LABEL')"
        :placeholder="t('INBOX_MGMT.ADD.REDLAB_CALLS.EXTENSION.PLACEHOLDER')"
        :message="
          formErrors.extension ||
          t('INBOX_MGMT.ADD.REDLAB_CALLS.EXTENSION.SUBTITLE')
        "
        :message-type="formErrors.extension ? 'error' : 'info'"
        @blur="v$.extension?.$touch"
      />

      <Input
        v-model="state.phoneNumber"
        :label="t('INBOX_MGMT.ADD.REDLAB_CALLS.PHONE_NUMBER.LABEL')"
        :placeholder="t('INBOX_MGMT.ADD.REDLAB_CALLS.PHONE_NUMBER.PLACEHOLDER')"
      />

      <div>
        <NextButton
          :is-loading="uiFlags.isCreating"
          :disabled="isSubmitDisabled"
          :label="t('INBOX_MGMT.ADD.REDLAB_CALLS.SUBMIT_BUTTON')"
          type="submit"
        />
      </div>
    </form>
  </div>
</template>
